import React, { useState, useEffect, useCallback } from 'react';
import { Input, Empty, Spin, Badge, Tooltip, Button } from 'antd';
import {
  SearchOutlined,
  CloseOutlined,
  MessageOutlined,
  LikeFilled,
  CheckCircleFilled,
  ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

/**
 * Collapsible right-side panel showing conversation history.
 *
 * Props:
 *   isOpen           {boolean}  Panel expanded/collapsed state
 *   onClose          {Function} Collapse the panel
 *   conversations    {Array}    List of conversation summaries
 *   loading          {boolean}  Whether conversations are loading
 *   onSelect         {Function} Callback when user clicks a conversation (session_id)
 *   onSearch         {Function} Callback when user types in search (keyword)
 *   activeSessionId  {string}   Currently loaded conversation's session_id
 */
function ConversationHistoryPanel({
  isOpen,
  onClose,
  conversations = [],
  loading = false,
  onSelect,
  onSearch,
  activeSessionId,
}) {
  const [searchText, setSearchText] = useState('');

  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    setSearchText(val);
    if (onSearch) onSearch(val);
  }, [onSearch]);

  if (!isOpen) return null;

  return (
    <div style={{
      width: 300,
      minWidth: 300,
      height: '100%',
      borderLeft: '1px solid var(--theme-border, #e8e8e8)',
      background: 'var(--theme-contentBg, #fff)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid var(--theme-border, #e8e8e8)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--theme-text, #262626)' }}>
          <ClockCircleOutlined style={{ marginRight: 6 }} />
          Conversations
        </span>
        <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} />
      </div>

      {/* Search */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--theme-border, #f0f0f0)' }}>
        <Input
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          placeholder="Search conversations..."
          value={searchText}
          onChange={handleSearchChange}
          allowClear
          size="small"
        />
      </div>

      {/* Conversations list */}
      <div style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="small" />
          </div>
        ) : conversations.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No previous conversations"
            style={{ padding: '40px 16px' }}
          >
            <span style={{ fontSize: 12, color: '#8c8c8c' }}>
              Start a conversation to see history here
            </span>
          </Empty>
        ) : (
          conversations.map((conv) => (
            <div
              key={conv.session_id}
              onClick={() => onSelect && onSelect(conv.session_id)}
              style={{
                padding: '10px 16px',
                cursor: 'pointer',
                borderBottom: '1px solid var(--theme-border, #f5f5f5)',
                background: activeSessionId === conv.session_id
                  ? 'var(--theme-selectedBg, #e6f4ff)'
                  : 'transparent',
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => {
                if (activeSessionId !== conv.session_id)
                  e.currentTarget.style.background = 'var(--theme-hoverBg, #fafafa)';
              }}
              onMouseLeave={(e) => {
                if (activeSessionId !== conv.session_id)
                  e.currentTarget.style.background = 'transparent';
              }}
            >
              {/* First question */}
              <div style={{
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--theme-text, #262626)',
                lineHeight: 1.4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {conv.first_question || 'Untitled conversation'}
              </div>

              {/* Meta row */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 4,
                fontSize: 11,
                color: 'var(--theme-textSecondary, #8c8c8c)',
              }}>
                <span>{dayjs(conv.last_timestamp).fromNow()}</span>
                <Badge
                  count={conv.message_count}
                  size="small"
                  style={{ backgroundColor: 'var(--theme-primary)' }}
                  overflowCount={99}
                />
                {conv.has_liked && (
                  <Tooltip title="Contains liked answers">
                    <LikeFilled style={{ color: '#1677ff', fontSize: 11 }} />
                  </Tooltip>
                )}
                {conv.has_verified && (
                  <Tooltip title="Contains verified answers">
                    <CheckCircleFilled style={{ color: '#52c41a', fontSize: 11 }} />
                  </Tooltip>
                )}
              </div>

              {/* Preview */}
              {conv.preview_answer && (
                <div style={{
                  fontSize: 11,
                  color: 'var(--theme-textSecondary, #a0a0a0)',
                  marginTop: 4,
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {conv.preview_answer}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ConversationHistoryPanel;
